import React, { useEffect, useMemo, useRef, useState } from 'react'
import ChatBody from './ChatBody'
import ChatFooter from './ChatFooter'
import './chat.css'
import etourneau from '../../../assets/images/image_oiseaux/etourneau.png'
import { io } from 'socket.io-client'
import { getNotifications, setNotificationWatched } from '../../../api/Notifications'

type Chat = {
  createdAt: string
  email: string
  id: string
  otherUser: {
    firstname: string
    lastname: string
    city: string
    userImg: string
    email: string
  }
  status: number
  updatedAt: string
  userImg: string
}

type Message = {
  chatId: string
  content: string
  createdAt: Date
  id: string
  updatedAt: Date
  userEmail: string
}

type UserOnline = {
  socketID: string
  userId: string
}

interface ChatPageProps {
  authState: {
    email: string
    firstname: string
    lastname: string
    phoneNumber: string
    createdAt: Date
    updatedAt: Date
    userImg: string
    birthdate: string
    city: string
    isVisiteur: boolean
  }

  chat: Chat | undefined
  token: string
}

const ChatPage = ({ chat, authState, token }: ChatPageProps) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [typingStatus] = useState('')
  const lastMessageRef = useRef(null)
  const [isUsersOnline, setIsUsersOnline] = useState(false)

  const serverAppDomain = process.env.REACT_APP_WEBSOCKET_URL
  const socket = useMemo(() => io(serverAppDomain as string, { transports: ['websocket'] }), [])

  useEffect(() => {
    return () => {
      socket.disconnect()
    }
  }, [socket])

  const getNotif = async () => {
    // get all notif from this chat and set all watched
    const res = await getNotifications(token, authState.email)
    if (chat) {
      const filteredNotifs = res.notifications.filter((notif: any) => {
        return notif.content.chatId === chat.id && !notif.isWatched
      })

      for (const notif of filteredNotifs) {
        await setNotificationWatched(token, notif.notifId)
      }
    }
  }

  // Reset messages quand on change de conversation (évite mélange / "disparitions")
  useEffect(() => {
    setMessages([])
  }, [chat?.id])

  useEffect(() => {
    const handler = (data: any) => {
      // On n'affiche que les messages du chat courant
      if (!chat?.id || data?.chatId !== chat.id) return
      // Eviter le doublon côté émetteur (on ajoute déjà en local dans ChatFooter)
      if (data?.socketID && data.socketID === socket.id) return

      setMessages((prev) => {
        if (data?.id && prev.some((m) => m.id === data.id)) return prev
        return [...prev, data]
      })
    }

    socket.on('messageResponse', handler)
    return () => {
      socket.off('messageResponse', handler)
    }
  }, [chat?.id, socket])

  useEffect(() => {
    getNotif()
  }, [chat?.id])
  useEffect(() => {
    const handler = (data: UserOnline[]) => {
      setIsUsersOnline(data.some((user) => user.userId === chat?.otherUser.email))
    }
    socket.on('onlineUsersUpdate', handler)
    return () => {
      socket.off('onlineUsersUpdate', handler)
    }
  }, [chat?.otherUser.email, socket])

  return (
    <div className='chat'>
      <div className='message__header'>
        <div className='flex items-center justify-center gap-4'>
          <div className='message__header-img-profil'>
            {isUsersOnline && <div className='round-user-Login'></div>}
            <img src={chat?.otherUser.userImg ? chat?.otherUser.userImg : etourneau} alt='man' />
          </div>
          <div className='flex gap-1 w-fil'>
            <p className='text-base font-medium first-letter:uppercase'>
              {chat?.otherUser?.firstname}
            </p>
            <p className='text-base text-gray-500'>{chat?.otherUser?.lastname?.charAt(0)}.</p>
          </div>
        </div>
      </div>

      <ChatBody
        messages={messages}
        typingStatus={typingStatus}
        lastMessageRef={lastMessageRef}
        setMessages={setMessages}
        chatId={chat?.id}
        token={token}
        otherUser={chat?.otherUser}
      />
      <ChatFooter
        chatId={chat?.id}
        socket={socket}
        setMessages={setMessages}
        token={token}
        authState={authState}
        otherUserEmail={chat?.otherUser.email}
      />
    </div>
  )
}

export default ChatPage
